import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, ChevronDown, ArrowUpRight } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const pad = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// "Publicações de hoje" = disponibilizadas hoje OU capturadas pelo app hoje (a busca pode
// trazer com atraso uma publicação de ontem — ela também é novidade para quem abre o painel).
function isToday(item) {
  const today = localISO(new Date());
  if (String(item.published_at || "").slice(0, 10) === today) return true;
  if (item.created_at) {
    const d = new Date(item.created_at);
    if (!Number.isNaN(d.getTime()) && localISO(d) === today) return true;
  }
  return false;
}

// Lote 69 — botão dividido (mesmo estilo do antigo "Exportar"): clicar no corpo abre a aba
// Publicações; clicar na seta abre os resumos das publicações do dia, os mesmos textos da aba.
export default function PublicacoesSplitButton({ unread = 0 }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await http.get("/publicacoes", { params: { limit: 60 } });
      setItems(r.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const today = (items || []).filter(isToday);
  const shown = today.slice(0, 6);

  return (
    <div className="inline-flex items-stretch" data-testid="publicacoes-split-button">
      <Link to="/publicacoes" className="flex">
        <Button
          data-testid="publicacoes-shortcut-button"
          variant="outline"
          size="sm"
          className="gap-2 border-stone-300 hover:bg-stone-100 rounded-r-none border-r-0 h-8"
        >
          <Mail className="w-4 h-4" strokeWidth={1.75} /> Publicações
          {unread > 0 && (
            <span
              data-testid="publicacoes-unread-badge"
              className="ml-0.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-emerald-brand text-white text-[10px] font-bold tabular-nums"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </Link>
      <DropdownMenu onOpenChange={(o) => { if (o) loadItems(); }}>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="publicacoes-summary-toggle"
            variant="outline"
            size="sm"
            aria-label="Ver resumos das publicações de hoje"
            className="border-stone-300 hover:bg-stone-100 rounded-l-none px-2 h-8"
          >
            <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0">
          <div className="px-4 py-2.5 border-b border-stone-200 text-[11px] font-bold tracking-widest uppercase text-stone-500">
            Publicações de hoje{items ? ` (${today.length})` : ""}
          </div>
          <div className="max-h-80 overflow-auto" data-testid="publicacoes-summary-list">
            {loading && <p className="px-4 py-4 text-sm text-stone-500">Carregando…</p>}
            {!loading && items && shown.length === 0 && (
              <p className="px-4 py-4 text-sm text-stone-500">
                Nenhuma publicação nova hoje. Veja o histórico completo na aba Publicações.
              </p>
            )}
            {!loading && shown.map((it) => (
              <button
                key={it.external_id}
                type="button"
                data-testid={`publicacoes-summary-${it.external_id}`}
                onClick={() => navigate("/publicacoes")}
                className="w-full text-left px-4 py-2.5 border-b border-stone-100 last:border-b-0 hover:bg-stone-50"
              >
                <div className="flex items-center gap-2">
                  {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-brand shrink-0" aria-label="Não lida" />}
                  <span className={`text-sm line-clamp-2 ${it.read ? "text-stone-700" : "font-semibold text-stone-900"}`}>
                    {it.summary || it.court_origin || "Publicação"}
                  </span>
                </div>
                <div className="text-[11px] text-stone-400 mt-0.5 tabular-nums truncate">
                  {[it.case_number, it.court_origin].filter(Boolean).join(" · ")}
                </div>
              </button>
            ))}
          </div>
          <Link
            to="/publicacoes"
            className="flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-emerald-brand hover:bg-stone-50 border-t border-stone-200"
          >
            {today.length > shown.length ? `Ver todas (+${today.length - shown.length} hoje)` : "Abrir aba Publicações"}
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
